"use client";

import { Fragment } from "react";
import { cx } from "@frontend/components/ui";
import { formatINR } from "@creators/backend/lib/money";
import {
  SCHEDULE_PRESETS,
  planMilestones,
  type SchedulePresetKey,
} from "@creators/backend/lib/collabs/schedule";
import type { MilestoneTrigger } from "@creators/backend/lib/collabs/state-machine";

/** Short enough to sit beside an amount without wrapping the row. */
const TRIGGER_SHORT: Record<MilestoneTrigger, string> = {
  on_accept: "up front",
  on_draft_approval: "on approval",
  on_publish: "when live",
  on_live_period_end: "after live",
};

/**
 * How the agreed amount is broken into tranches. Shared by the opening offer and
 * the counter, because the split is as negotiable as the number itself — whoever
 * is being asked to pay half up front should be able to answer with a third.
 *
 * Stacked rows rather than three columns. The plans hold one, two and three
 * tranches, so equal-height cards left the shortest of them two-thirds empty,
 * and a column narrow enough for three-across had no room to put an amount next
 * to the moment it falls due. Stacked, each plan's split sits under the last,
 * which is what makes them comparable.
 */
export function PaymentPlanPicker({
  name,
  value,
  onChange,
  amountPaise,
  label = "Payment plan",
  variant = "stacked",
}: {
  /** Form field name — the offer and counter endpoints spell it differently. */
  name: string;
  value: SchedulePresetKey;
  onChange: (key: SchedulePresetKey) => void;
  amountPaise: number | null;
  label?: string;
  /** Stacked rows in the counter dialog; three pills on the offer form. */
  variant?: "stacked" | "inline";
}) {
  if (variant === "inline") {
    return (
      <InlinePaymentPlan
        name={name}
        value={value}
        onChange={onChange}
        amountPaise={amountPaise}
        label={label}
      />
    );
  }

  return (
    <StackedPaymentPlan
      name={name}
      value={value}
      onChange={onChange}
      amountPaise={amountPaise}
      label={label}
    />
  );
}

function InlinePaymentPlan({
  name,
  value,
  onChange,
  amountPaise,
  label,
}: {
  name: string;
  value: SchedulePresetKey;
  onChange: (key: SchedulePresetKey) => void;
  amountPaise: number | null;
  label: string;
}) {
  const keys = Object.keys(SCHEDULE_PRESETS) as SchedulePresetKey[];
  const preset = SCHEDULE_PRESETS[value];
  // Only a positive amount is split into rupees — never show ₹0 rows.
  const planned =
    amountPaise && amountPaise > 0
      ? planMilestones({ amountTotal: amountPaise, schedule: preset.schedule })
      : null;

  const parts = preset.schedule.map((item, index) => ({
    pct: item.pct,
    when: TRIGGER_SHORT[item.trigger],
    amount: planned ? formatINR(planned[index]!.amount) : null,
  }));

  return (
    <div className="min-w-0">
      <p className="text-[13px] font-medium text-ink-muted">{label}</p>

      <div className="mt-1.5 grid h-11 grid-cols-3 gap-1.5" role="radiogroup" aria-label={label}>
        {keys.map((key) => {
          const selected = value === key;
          return (
            <label
              key={key}
              className={cx(
                "inline-flex h-11 cursor-pointer items-center justify-center rounded-xl border px-2 text-center text-sm font-medium transition",
                selected
                  ? "border-brand-600 bg-brand-600 text-canvas"
                  : "border-line bg-surface text-ink hover:bg-canvas",
                "has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-brand-600",
              )}
            >
              <input
                type="radio"
                name={name}
                value={key}
                checked={selected}
                onChange={() => onChange(key)}
                className="sr-only"
              />
              <span className="truncate">{SCHEDULE_PRESETS[key].label}</span>
            </label>
          );
        })}
      </div>

      {/* One path under the selected plan — not a list of boxes. */}
      <p className="mt-2 pl-3 text-[13px] leading-snug text-ink-muted sm:pl-4">
        {parts.map((part, index) => (
          <Fragment key={part.when}>
            {index > 0 ? (
              <span aria-hidden="true" className="mx-1.5 text-ink-subtle">
                →
              </span>
            ) : null}
            <span>
              <span className="tabular font-semibold text-ink">
                {part.amount ?? `${part.pct}%`}
              </span>{" "}
              {part.when}
            </span>
          </Fragment>
        ))}
      </p>
    </div>
  );
}

function StackedPaymentPlan({
  name,
  value,
  onChange,
  amountPaise,
  label,
}: {
  name: string;
  value: SchedulePresetKey;
  onChange: (key: SchedulePresetKey) => void;
  amountPaise: number | null;
  label: string;
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="text-[13px] font-medium text-ink-muted">{label}</legend>

      <div className="mt-2 divide-y divide-line/70 overflow-hidden rounded-xl border border-line">
        {(Object.keys(SCHEDULE_PRESETS) as SchedulePresetKey[]).map((key) => {
          const preset = SCHEDULE_PRESETS[key];
          const selected = value === key;

          // Only a positive amount is split into rupees — never show ₹0 rows.
          const planned =
            amountPaise && amountPaise > 0
              ? planMilestones({ amountTotal: amountPaise, schedule: preset.schedule })
              : null;

          const parts = preset.schedule.map((item, index) => ({
            pct: item.pct,
            when: TRIGGER_SHORT[item.trigger],
            amount: planned ? formatINR(planned[index]!.amount) : `${item.pct}%`,
          }));

          return (
            <label
              key={key}
              className={cx(
                "flex cursor-pointer items-start gap-3 px-3.5 py-3 transition",
                // The input is visually hidden, so the row has to show its own focus.
                "has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-brand-600",
                // The filled dot and the darkened split bar are what mark the
                // choice. brand-50 is barely a shade off canvas, so the tint is
                // only a grouping cue and is not carrying the state on its own.
                selected ? "bg-brand-50" : "bg-surface hover:bg-canvas",
              )}
            >
              <input
                type="radio"
                name={name}
                value={key}
                checked={selected}
                onChange={() => onChange(key)}
                className="sr-only"
              />

              <span
                aria-hidden="true"
                className={cx(
                  "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition",
                  selected ? "border-brand-600 bg-brand-600" : "border-line-strong bg-surface",
                )}
              >
                {selected ? <span className="size-1.5 rounded-full bg-canvas" /> : null}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-ink">{preset.label}</span>

                {/* The shape of the split, so "how much up front" lands before any
                    of the numbers are read. */}
                <span aria-hidden="true" className="mt-2 flex h-1 gap-0.5">
                  {parts.map((part) => (
                    <span
                      key={part.when}
                      style={{ width: `${part.pct}%` }}
                      className={cx(
                        "block rounded-full",
                        selected ? "bg-brand-600" : "bg-line-strong",
                      )}
                    />
                  ))}
                </span>

                <span className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5 text-xs leading-relaxed">
                  {parts.map((part, index) => (
                    <Fragment key={part.when}>
                      {index > 0 ? <span className="text-ink-subtle">·</span> : null}
                      <span>
                        <span className="tabular font-semibold text-ink">{part.amount}</span>{" "}
                        <span className="text-ink-muted">{part.when}</span>
                      </span>
                    </Fragment>
                  ))}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
